/**
 * Sessions LIST over the shared repo — the first real surface
 * migration (post-R8). Requires an ancestor <TabRepoProvider>.
 *
 * Reads ONLY the project doc, via stock useDocument: list rows come from
 * the `sessionMeta` registry (title/updatedAt, maintained by session
 * registration) plus `sessionIds` for membership — the RFC 0233 scoped-list
 * contract, no session hydration. The shared repo fetches the project doc
 * on demand over subduction (find-by-need, the patchwork walk); deliberately
 * NO bootstrapProject here — pulling every session's children for a list
 * would resurrect the 20→1360 over-fetch this rebuild deleted. Sessions
 * hydrate when OPENED (a later surface).
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  registryStatus,
  type SessionRunStatus,
  sessionTitleOf,
} from '@mod/core/model'
import { usePrivateSessions } from '../../services/sessions/usePrivateSessions'
import { useDocument } from '../../services/world/repo/useDocument'

export interface SessionListRow {
  id: string
  title: string
  updatedAt?: number
  /** When it was filed as done, by a person or an agent (RFC 0286). The only
   *  part of a session's status that arrives with the registry read, no
   *  hydration. */
  completedAt?: number
  /** When `status` was claimed (isSessionDone reads it). */
  statusAt?: number
  /** When a message was last sent to it: what says a filed session has
   *  started again (isSessionDone). */
  sentAt?: number
  /**
   * What it is doing, when the registry can say (RFC 0284 S8a). Absent when no
   * runtime has reported, or the report went stale. Read off the registry so a
   * list can light a row WITHOUT opening the session: the list used to open the
   * top twenty session documents for this, and each one stayed in wasm memory
   * for the life of the window.
   */
  status?: SessionRunStatus
  /** The session that opened this one, when an agent did. Absent means a person
   *  started it. CARRIED, not filtered on: whether a sub-session belongs in a
   *  given list is the list's decision, and this hook feeds more than one. */
  parentSessionId?: string
}

interface ProjectListView {
  sessionIds?: string[]
  sessionMeta?: Record<
    string,
    {
      title?: string
      updatedAt?: number
      completedAt?: number
      status?: unknown
      statusAt?: unknown
      sentAt?: unknown
      parentSessionId?: string
    }
  >
}

export function useSessionList(projectId: string | undefined): {
  sessions: SessionListRow[]
  loading: boolean
} {
  const [project] = useDocument<ProjectListView>(
    projectId as AnyDocumentId | undefined,
  )

  const privateSessions = usePrivateSessions()
  if (privateSessions.enabled && privateSessions.loading)
    return { sessions: [], loading: true }

  if (!project) return { sessions: [], loading: projectId != null }

  // A session shared with you sits in the list whatever project is open: you
  // were given the session, not its project (RFC 0345 3c).
  const shared = Object.fromEntries(
    Object.entries(privateSessions.shared ?? {})
      .filter(([, entry]) => entry.kind === 'session')
      .map(([id, entry]) => [
        id,
        {
          title: entry.title,
          titleSetBy: 'user' as const,
          updatedAt: entry.joinedAt,
        },
      ]),
  )
  const meta: NonNullable<ProjectListView['sessionMeta']> =
    privateSessions.enabled
      ? {
          ...shared,
          ...Object.fromEntries(
            Object.entries(privateSessions.entries ?? {})
              .filter(([, entry]) => entry.projectId === projectId)
              .map(([id, entry]) => [
                id,
                {
                  title: entry.title?.toString(),
                  updatedAt: entry.updatedAt,
                  completedAt: entry.completedAt,
                  status: entry.status,
                  statusAt: entry.statusAt,
                  sentAt: entry.sentAt,
                  parentSessionId: entry.parentSessionId,
                },
              ]),
          ),
        }
      : (project.sessionMeta ?? {})
  const now = Date.now()
  const sessions = (
    privateSessions.enabled ? Object.keys(meta) : (project.sessionIds ?? [])
  )
    .map((id) => {
      const entry = meta[String(id)]
      const status = registryStatus(entry, now)
      return {
        id: String(id),
        title:
          sessionTitleOf(entry as never) || `Session ${String(id).slice(0, 8)}`,
        updatedAt: entry?.updatedAt,
        completedAt: entry?.completedAt,
        // When the status was claimed: half of what says a filed session has
        // started again (isSessionDone).
        ...(typeof entry?.statusAt === 'number'
          ? { statusAt: entry.statusAt }
          : {}),
        ...(typeof entry?.sentAt === 'number' ? { sentAt: entry.sentAt } : {}),
        ...(status ? { status } : {}),
        parentSessionId: entry?.parentSessionId
          ? String(entry.parentSessionId)
          : undefined,
      }
    })
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))

  return { sessions, loading: false }
}
